package com.qrkontaq.pages.invite

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.qrkontaq.core.auth.Session
import com.qrkontaq.data.repository.AdminRepository
import com.qrkontaq.di.LocalAppContainer
import com.qrkontaq.ui.admin.AdminIcons
import com.qrkontaq.ui.admin.AdminT
import com.qrkontaq.ui.admin.AuthStatusView
import com.qrkontaq.ui.admin.LoginView
import com.qrkontaq.ui.admin.roleLabel
import com.qrkontaq.utils.PageSeo
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext

private sealed interface InviteState {
    data object Booting : InviteState

    data object NeedsLogin : InviteState

    data object Accepting : InviteState

    data class Joined(val role: String) : InviteState

    data object Failed : InviteState
}

/** Invite link target: {baseDomain}/invite/{code}. Signs in if needed, then joins the team. */
@Page("{code}")
@Composable
fun InvitePage() {
    PageSeo(title = "QRkontaq — ${AdminT.inviteWelcome}", description = "")

    val context = rememberPageContext()
    val container = LocalAppContainer.current
    val code = context.route.params["code"].orEmpty()

    var state by remember { mutableStateOf<InviteState>(InviteState.Booting) }
    var reloadKey by remember { mutableStateOf(0) }

    val adminRepository =
        remember(Session.accessToken) {
            Session.accessToken?.let { token ->
                container.createAuthenticatedClients(token)
                AdminRepository(container.getAuthenticatedClient())
            }
        }

    LaunchedEffect(reloadKey, code) {
        if (code.isBlank()) {
            state = InviteState.Failed
            return@LaunchedEffect
        }
        if (!Session.isLoggedIn || adminRepository == null) {
            state = InviteState.NeedsLogin
            return@LaunchedEffect
        }
        state = InviteState.Accepting
        val response = runCatching { adminRepository.acceptInvite(code) }.getOrNull()
        if (response != null) {
            Session.activeRestaurantId = response.restaurantId
            state = InviteState.Joined(response.role)
        } else {
            state = InviteState.Failed
        }
    }

    when (val current = state) {
        is InviteState.Booting -> Unit

        is InviteState.NeedsLogin ->
            LoginView(notice = AdminT.inviteLoginHint) { reloadKey++ }

        is InviteState.Accepting ->
            AuthStatusView(icon = null, title = AdminT.inviteAccepting, tone = "neutral")

        is InviteState.Joined ->
            AuthStatusView(
                icon = AdminIcons.Check,
                title = AdminT.inviteJoined(roleLabel(current.role)),
                tone = "success",
                actionText = AdminT.inviteGoCabinet,
                onAction = { context.router.navigateTo("/admin") },
            )

        is InviteState.Failed ->
            AuthStatusView(
                icon = AdminIcons.Ban,
                title = AdminT.inviteInvalid,
                tone = "danger",
                actionText = AdminT.inviteGoCabinet,
                onAction = { context.router.navigateTo("/admin") },
            )
    }
}
